// userActions.js
// This is the actions for the user reducer, needs to be applied to top level component

import {
  initializeUserSession,
  handleLogout,
  login,
  checkSessionValidity,
} from "../../auth/auth";
import { UserActionTypes } from "./user.reducer";
import { clearAllData, setLoggingOut } from "../../services/IndexedDBService";

// Flag to prevent session validation during logout
let isCurrentlyLoggingOut = false;

// This action handles the initial session check on app load
export const loadUserSession = async (dispatch) => {
  dispatch({ type: UserActionTypes.SESSION_FETCH_START });
  try {
    const user = await initializeUserSession();
    dispatch({
      type: UserActionTypes.SESSION_FETCH_SUCCESS,
      payload: user,
    });
  } catch (err) {
    // Don't show error for missing sessions
    if (err.message === "No user session found") {
      dispatch({
        type: UserActionTypes.SESSION_FETCH_FAILURE,
        payload: null, // No error message for missing sessions
      });
    } else {
      // Show error for actual network/server issues
      dispatch({
        type: UserActionTypes.SESSION_FETCH_FAILURE,
        payload: err.message,
      });
    }
  }
};

// This action handles the login process
export const loginUser = async (
  dispatch,
  Navigate,
  username,
  password,
  nextUrl
) => {
  dispatch({ type: UserActionTypes.LOGIN_START });

  try {
    const data = await login(username, password);

    if (data.first_login) {
      dispatch({
        type: UserActionTypes.FIRST_LOGIN_SUCCESS,
        payload: data.user,
      });
      Navigate("/password_change");
    } else {
      dispatch({
        type: UserActionTypes.LOGIN_SUCCESS,
        payload: data.user,
      });

      // Clear IndexedDB to ensure fresh state on new login
      await clearAllData();

      // Honor next parameter if safe (internal path only)
      const safeNext =
        typeof nextUrl === "string" && nextUrl.startsWith("/") ? nextUrl : null;
      if (safeNext) {
        Navigate(safeNext);
      } else {
        Navigate("/home");
      }
    }
  } catch (err) {
    dispatch({
      type: UserActionTypes.LOGIN_FAILURE,
      payload: err.message,
    });
  }
};

// This action handles logging the user out
export const logoutUser = async (dispatch, navigate) => {
  // Set flag to prevent session validation during logout
  isCurrentlyLoggingOut = true;
  setLoggingOut(true);

  dispatch({ type: UserActionTypes.LOGOUT_START });

  try {
    // Call the logout API
    await handleLogout();

    // Clear IndexedDB completely
    await clearAllData();

    // Explicitly reset the user slice (new provider)
    dispatch({ type: UserActionTypes.LOGOUT_SUCCESS });

    // Navigate to login page
    if (navigate) {
      navigate("/", { replace: true });
    }
  } catch (err) {
    // Even on logout error, we clear the user from the state
    try {
      await clearAllData();
      dispatch({ type: UserActionTypes.LOGOUT_SUCCESS });
    } catch (cleanupError) {
      console.error("Failed to cleanup on logout error:", cleanupError);
    }

    // Still navigate to login on error
    if (navigate) {
      navigate("/", { replace: true });
    }
  } finally {
    // Reset flags after logout is complete - small delay to ensure cleanup
    setTimeout(() => {
      setLoggingOut(false);
      isCurrentlyLoggingOut = false;
    }, 100);
  }
};

// Enhanced session validation that respects logout state
export const validateUserSession = async (dispatch) => {
  // Don't validate session if we're currently logging out
  if (isCurrentlyLoggingOut) {
    return;
  }

  dispatch({ type: UserActionTypes.SESSION_VALIDATION_START });
  try {
    const isValid = await checkSessionValidity();

    // Double-check we're not logging out before processing response
    if (!isCurrentlyLoggingOut) {
      if (isValid) {
        // Session is valid - just mark validation as successful
        // Don't send any payload since we want to keep existing user data
        dispatch({
          type: UserActionTypes.SESSION_VALIDATION_SUCCESS,
        });
      } else {
        // Session is invalid - this should trigger logout
        throw new Error("Session Expired");
      }
    }
  } catch (err) {
    // Don't show errors if we're logging out
    if (!isCurrentlyLoggingOut) {
      dispatch({
        type: UserActionTypes.SESSION_VALIDATION_FAILURE,
        payload: err.message,
      });
      // Re-throw to trigger logout in the hook
      throw err;
    }
  }
};

// Export the logout flag for other components to check
export const isLoggingOut = () => isCurrentlyLoggingOut;
